import api from './axios';
import {
  AdminLoginRequest,
  AdminLoginResponse,
  AdminRegisterRequest,
} from './types/adminAuth';
import { clearAdminAuthCookies, setAdminAuthCookies } from '@/lib/cookies';

interface AdminLoginResult {
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
    adminRole?: string;
  };
  token: string;
}

export const adminAuthService = {
  async login(credentials: AdminLoginRequest): Promise<AdminLoginResult> {
    try {
      const response = await api.post<AdminLoginResponse>(
        '/authentication/admin-user-login',
        credentials
      );

      const data = response.data;
      
      // Extract auth token from admin_session
      const authToken = data.data.admin_session.auth_key;
      
      if (!authToken) {
        throw new Error('No auth token found in response');
      }

      // Store in localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('adminToken', authToken);
        localStorage.setItem('token', authToken); // For compatibility
        localStorage.setItem('adminUserId', data.data.admin_user.id);
      }

      // Create user object
      const user = {
        id: data.data.admin_user.id,
        name: `${data.data.admin_profile.admin_first_name.String} ${data.data.admin_profile.admin_last_name.String}`.trim(),
        email: data.data.admin_user.admin_user_email,
        role: 'admin' as const,
        adminRole: data.data.admin_profile.admin_role.String,
      };

      // Store user in localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('adminUser', JSON.stringify(user));
      }

      // Set cookies with expiration
      const expirationDate = data.data.admin_session.expired_at?.Valid && data.data.admin_session.expired_at?.Time
        ? new Date(data.data.admin_session.expired_at.Time)
        : new Date(Date.now() + 48 * 60 * 60 * 1000); // 48 hours default

      // Set admin auth cookies
      setAdminAuthCookies(authToken, {
        user,
        admin_profile: data.data.admin_profile,
        admin_session: data.data.admin_session,
        admin_user: data.data.admin_user
      }, expirationDate);

      return {
        user,
        token: authToken,
      };
    } catch (error: any) {
      if (error.response) {
        console.error('📡 [adminAuthService] Error response:', {
          status: error.response.status,
          data: error.response.data,
        });
      }

      throw error;
    }
  },

  async register(userData: AdminRegisterRequest): Promise<any> {
    try {
      const response = await api.post(
        '/authentication/admin-user-register',
        userData
      );
      return response.data;
    } catch (error: any) {
      if (error.response) {
        console.error('📡 [adminAuthService] Registration error:', {
          status: error.response.status,
          data: error.response.data,
        });
      }
      throw error;
    }
  },

  isAdminLoggedIn(): boolean {
    if (typeof window === 'undefined') return false;

    const adminToken = localStorage.getItem('adminToken');
    return !!adminToken;
  },

  logout(): void {
    if (typeof window === 'undefined') return;
    
    // Remove all admin-related localStorage items
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUserId');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('token'); // Also remove the generic token
    
    // Clear cookies
    clearAdminAuthCookies();
  },

  getAdminToken(): string | null {
    if (typeof window === 'undefined') return null;

    return localStorage.getItem('adminToken');
  },
};

export default adminAuthService;
